/**
 * Dashboard Router
 *
 * Wrapper component that determines which dashboard to display based on
 * restaurant's active subscriptions and product selection.
 *
 * Flow:
 * 1. Fetch navigation state from API
 * 2. Show ProductSwitcher if multiple subscriptions
 * 3. Render appropriate dashboard:
 *    - Virtual Assistant → RestaurantDashboard (existing)
 *    - Virtual Host → VirtualHostControlCenter (new)
 *
 * Author: Dine AI
 * Created: November 20, 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import RestaurantDashboard from './RestaurantDashboard';
import VirtualHostControlCenter from '@/components/virtual-host/control-center/VirtualHostControlCenter';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';

interface NavigationState {
  active_product: 'virtual_assistant' | 'virtual_host';
  subscriptions: Array<{
    type: string;
    plan: string;
    status: string;
  }>;
  show_tabs: boolean;
  virtual_assistant_available: boolean;
  virtual_host_available: boolean;
}

const DashboardRouter: React.FC = () => {
  const navigate = useNavigate();
  const [navigationState, setNavigationState] = useState<NavigationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch dashboard navigation state
   */
  const fetchNavigationState = async () => {
    try {
      const token = Cookies.get('auth_token');

      // For OAuth users, add Authorization header
      // For demo users, session cookie is sent automatically
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/dashboard/navigation', {
        headers,
        credentials: 'include'  // Include cookies for session auth
      });

      if (!response.ok) {
        if (response.status === 401) {
          Cookies.remove('auth_token');
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch navigation state');
      }

      const data = await response.json();
      setNavigationState(data);
    } catch (error) {
      console.error('Error fetching navigation state:', error);
      setError('Failed to load dashboard. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchNavigationState();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !navigationState) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-xl p-8 border border-gray-200">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Dashboard Error</h2>
            <p className="text-gray-600 mb-6">{error || 'Failed to load dashboard'}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#FDB913] text-black rounded-lg font-medium hover:bg-[#e5a711] transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine which dashboard to show
  const showVirtualHost = navigationState.active_product === 'virtual_host';
  const showVirtualAssistant = navigationState.active_product === 'virtual_assistant';

  return (
    <div className="fixed inset-0 bg-gray-50 overflow-auto">
      <DashboardSidebar
        activeProduct={navigationState.active_product}
        virtualAssistantAvailable={navigationState.virtual_assistant_available}
        virtualHostAvailable={navigationState.virtual_host_available}
        showSidebar={navigationState.show_tabs}
      >
      {(sidebarExpanded) => (
        <>
          {/* Conditional Dashboard Rendering */}
          {showVirtualAssistant && navigationState.virtual_assistant_available && (
            <div className="min-h-screen flex flex-col">
              <RestaurantDashboard sidebarExpanded={sidebarExpanded} />
            </div>
          )}

          {showVirtualHost && navigationState.virtual_host_available && (
            <div className="min-h-screen flex flex-col">
              <VirtualHostControlCenter sidebarExpanded={sidebarExpanded} />
            </div>
          )}

          {/* No Active Subscription Fallback */}
          {!navigationState.virtual_assistant_available && !navigationState.virtual_host_available && (
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center max-w-md mx-auto px-4">
                <div className="bg-white rounded-lg shadow-xl p-8 border border-gray-200">
                  <div className="text-yellow-500 text-5xl mb-4">🔒</div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h2>
                  <p className="text-gray-600 mb-6">
                    Your account doesn't have an active subscription.
                    Please subscribe to access the dashboard.
                  </p>
                  <button
                    onClick={() => navigate('/products/virtual-assistant')}
                    className="px-6 py-3 bg-[#FDB913] text-black rounded-lg font-medium hover:bg-[#e5a711] transition-colors"
                  >
                    View Pricing
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardSidebar>
    </div>
  );
};

export default DashboardRouter;
